import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import './GrantDocuments.css';
import {
  JIT_DOCUMENT_ID_BASE,
  JIT_ADDITIONAL_DOCUMENTS_DOC_ID
} from '../utils/grantDocumentIds';

// Just-in-Time paperwork is a fixed checklist — unlike the application documents it
// doesn't vary with the questionnaire — so the IDs are simply the base plus an index.
// They must never be renumbered: an uploaded file is identified by its ID.
const JIT_DOCUMENTS = [
  { id: JIT_DOCUMENT_ID_BASE + 0, name: 'Evaluation of Financial Management System' },
  { id: JIT_DOCUMENT_ID_BASE + 1, name: 'Financial Statements' },
  { id: JIT_DOCUMENT_ID_BASE + 2, name: 'JIT Questions' },
  { id: JIT_DOCUMENT_ID_BASE + 3, name: 'CAP Table' },
  { id: JIT_DOCUMENT_ID_BASE + 4, name: 'PI Current and Pending Support', usesCompanyName: true },
  { id: JIT_DOCUMENT_ID_BASE + 5, name: 'SBIR Funding Agreement' },
  { id: JIT_DOCUMENT_ID_BASE + 6, name: 'SBIR Foreign Disclosure Form' }
];

const GrantJITDocuments = ({ userInfo }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRefs = useRef({});

  // Additional files: any number, each with its own note.
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [additionalDocNotes, setAdditionalDocNotes] = useState('');
  const [additionalDocUploading, setAdditionalDocUploading] = useState(false);
  const additionalDocFileInputRef = useRef(null);
  const [editingAdditionalDocId, setEditingAdditionalDocId] = useState(null);
  const [editingAdditionalDocNotes, setEditingAdditionalDocNotes] = useState('');
  const [savingAdditionalDocNotes, setSavingAdditionalDocNotes] = useState(false);

  // "[Company] PI Current and Pending Support" uses the name entered on the company
  // formation form, the same source the Upload Documents page uses.
  const [companyOfficialName, setCompanyOfficialName] = useState('');
  const companyName = companyOfficialName || userInfo?.selectedCompany || '';

  const getDocumentName = (document) =>
    document.usesCompanyName
      ? `${companyName ? `${companyName} ` : ''}${document.name}`
      : document.name;

  const isXlsxFile = (filename) => {
    if (!filename) return false;
    const extension = filename.toLowerCase().split('.').pop();
    return extension === 'xlsx' || extension === 'xls';
  };

  const uploadedCount = JIT_DOCUMENTS.filter(doc => uploadStatus[doc.id]?.uploaded).length;

  const handleFileDownload = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const parseMetadata = (doc) => {
    try {
      if (!doc.metadata) return {};
      return typeof doc.metadata === 'string' ? JSON.parse(doc.metadata) : doc.metadata;
    } catch (parseError) {
      return {};
    }
  };

  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch application details');
        }

        const data = await response.json();
        setApplication(data.application);

        const documents = data.application.documents || [];

        const status = {};
        documents.forEach(doc => {
          status[doc.documentId] = {
            uploaded: true,
            filename: doc.filename,
            uploadDate: doc.uploadDate,
            fileUrl: doc.fileUrl
          };
        });
        setUploadStatus(status);

        setAdditionalDocuments(
          documents
            .filter(doc => String(doc.documentId) === String(JIT_ADDITIONAL_DOCUMENTS_DOC_ID))
            .map(doc => ({
              id: doc.id,
              filename: doc.filename,
              uploadDate: doc.uploadDate,
              fileUrl: doc.fileUrl,
              notes: parseMetadata(doc).notes || ''
            }))
        );
      } catch (fetchError) {
        console.error('Error fetching application:', fetchError);
        setError('Failed to load application details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (applicationId) {
      fetchApplicationDetails();
    }
  }, [applicationId]);

  useEffect(() => {
    const fetchCompanyOfficialName = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`,
          { method: 'GET', credentials: 'include' }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (data.companyInfo?.companyName) {
          setCompanyOfficialName(data.companyInfo.companyName);
        }
      } catch (companyError) {
        console.error('Error fetching company info:', companyError);
      }
    };

    fetchCompanyOfficialName();
  }, []);

  // Shared upload path: signed URL, direct-to-storage PUT, then confirm.
  const uploadToApplication = async (documentId, file, filename, metadata) => {
    const getUrlResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/document-upload-url`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId, filename, contentType: file.type })
      }
    );

    if (!getUrlResponse.ok) {
      throw new Error('Failed to get upload URL');
    }

    const urlData = await getUrlResponse.json();

    const uploadResponse = await fetch(urlData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload document to cloud storage');
    }

    const confirmResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/confirm-document-upload`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          filename,
          destination: urlData.destination,
          downloadUrl: urlData.downloadUrl,
          ...(metadata ? { metadata } : {})
        })
      }
    );

    if (!confirmResponse.ok) {
      throw new Error('Failed to confirm document upload');
    }

    const confirmData = await confirmResponse.json();
    return { document: confirmData.document, downloadUrl: urlData.downloadUrl };
  };

  const handleFileUpload = async (documentId, file) => {
    try {
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: { ...prev[documentId], uploading: true, error: null }
      }));

      const { downloadUrl } = await uploadToApplication(documentId, file, file.name);

      setUploadStatus(prev => ({
        ...prev,
        [documentId]: {
          uploaded: true,
          filename: file.name,
          uploadDate: new Date().toISOString(),
          uploading: false,
          fileUrl: downloadUrl
        }
      }));
    } catch (uploadError) {
      console.error('Error uploading document:', uploadError);
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: {
          ...prev[documentId],
          uploading: false,
          error: `Failed to upload: ${uploadError.message}`
        }
      }));
    }
  };

  const handleAdditionalDocumentUpload = async (file, notes) => {
    if (!file) return;

    setAdditionalDocUploading(true);
    try {
      const timestamp = new Date().getTime();
      const uniqueFilename = `JIT_Additional_${timestamp}_${file.name}`;

      const { document: created, downloadUrl } = await uploadToApplication(
        JIT_ADDITIONAL_DOCUMENTS_DOC_ID,
        file,
        uniqueFilename,
        { notes: notes || '', originalFilename: file.name }
      );

      setAdditionalDocuments(prev => [
        ...prev,
        {
          id: created?.id || `${timestamp}`,
          filename: uniqueFilename,
          uploadDate: new Date().toISOString(),
          fileUrl: downloadUrl,
          notes: notes || ''
        }
      ]);
      setAdditionalDocNotes('');
      if (additionalDocFileInputRef.current) {
        additionalDocFileInputRef.current.value = '';
      }
    } catch (uploadError) {
      console.error('Error uploading additional document:', uploadError);
      alert(`Failed to upload document: ${uploadError.message}`);
    } finally {
      setAdditionalDocUploading(false);
    }
  };

  const handleSaveAdditionalDocNotes = async (docId) => {
    setSavingAdditionalDocNotes(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${docId}/metadata`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metadata: { notes: editingAdditionalDocNotes } })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save note');
      }

      setAdditionalDocuments(prev =>
        prev.map(doc => (doc.id === docId ? { ...doc, notes: editingAdditionalDocNotes } : doc))
      );
      setEditingAdditionalDocId(null);
      setEditingAdditionalDocNotes('');
    } catch (saveError) {
      console.error('Error updating document note:', saveError);
      alert(`Failed to save note: ${saveError.message}`);
    } finally {
      setSavingAdditionalDocNotes(false);
    }
  };

  const handleRemoveAdditionalDocument = async (documentRecordId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/documents/${documentRecordId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      setAdditionalDocuments(prev => prev.filter(doc => doc.id !== documentRecordId));
    } catch (removeError) {
      console.error('Error removing additional document:', removeError);
      alert(`Failed to remove document: ${removeError.message}`);
    }
  };

  const handleBatchDownload = async () => {
    const files = [
      ...JIT_DOCUMENTS
        .filter(doc => uploadStatus[doc.id]?.uploaded && uploadStatus[doc.id]?.fileUrl)
        .map(doc => ({ fileUrl: uploadStatus[doc.id].fileUrl, filename: uploadStatus[doc.id].filename })),
      ...additionalDocuments
        .filter(doc => doc.fileUrl)
        .map(doc => ({ fileUrl: doc.fileUrl, filename: doc.filename }))
    ];

    if (files.length === 0) {
      alert('No documents have been uploaded yet.');
      return;
    }

    const button = document.querySelector('.batch-download-btn');
    const originalText = button?.textContent;

    try {
      const zip = new JSZip();
      if (button) {
        button.textContent = 'Creating zip file...';
        button.disabled = true;
      }

      for (const file of files) {
        try {
          const response = await fetch(file.fileUrl);
          if (response.ok) {
            zip.file(file.filename || 'document', await response.blob());
          }
        } catch (fileError) {
          console.error(`Failed to fetch ${file.filename}:`, fileError);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `jit_documents_${applicationId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (zipError) {
      console.error('Error creating zip file:', zipError);
      alert('Failed to create zip file. Please try again.');
    } finally {
      if (button) {
        button.textContent = originalText;
        button.disabled = false;
      }
    }
  };

  const handleReturn = () => {
    navigate('/grant-submission');
  };

  if (isLoading) {
    return <div className="loading-indicator">Loading Just-in-Time documents...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const totalUploaded = uploadedCount + additionalDocuments.length;

  return (
    <div className="grant-documents-container">
      <div className="documents-header">
        <h1>Just-in-Time Documents</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="return-btn"
            onClick={() => navigate(`/grant-submission/documents/${applicationId}`)}
          >
            Application Documents
          </button>
          <button className="return-btn" onClick={handleReturn}>
            Return to Applications
          </button>
        </div>
      </div>

      <div className="application-info-bar">
        <h2>{application?.name}</h2>
        <div className="document-progress">
          <span>Progress: </span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(uploadedCount / JIT_DOCUMENTS.length) * 100}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {uploadedCount}/{JIT_DOCUMENTS.length} Documents Uploaded
          </span>
        </div>
        <div className="batch-download-section">
          <button
            className="batch-download-btn"
            onClick={handleBatchDownload}
            disabled={totalUploaded === 0}
            title={totalUploaded === 0 ? 'No documents uploaded yet' : 'Download all uploaded documents'}
          >
            Download All ({totalUploaded})
          </button>
        </div>
      </div>

      <div className="documents-list">
        {JIT_DOCUMENTS.map(document_ => (
          <div key={document_.id} className="document-card">
            <div className="document-header">
              <div className="document-title-block">
                <h4>{getDocumentName(document_)}</h4>
                {uploadStatus[document_.id]?.uploaded && (
                  <span className="document-meta">
                    {[
                      uploadStatus[document_.id].filename,
                      uploadStatus[document_.id].uploadDate
                        ? new Date(uploadStatus[document_.id].uploadDate).toLocaleDateString()
                        : null
                    ].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              <div className="document-header-actions">
                <span className={`status-indicator ${uploadStatus[document_.id]?.uploaded ? 'uploaded' : 'missing'}`}>
                  {uploadStatus[document_.id]?.uploaded ? 'Uploaded' : 'Required'}
                </span>
                {uploadStatus[document_.id]?.uploaded ? (
                  <div className="file-actions">
                    {uploadStatus[document_.id].fileUrl && (
                      <>
                        {!isXlsxFile(uploadStatus[document_.id].filename) && (
                          <button
                            className="view-btn"
                            onClick={() => window.open(uploadStatus[document_.id].fileUrl, '_blank')}
                          >
                            View
                          </button>
                        )}
                        <button
                          className="download-btn"
                          onClick={() => handleFileDownload(uploadStatus[document_.id].fileUrl, uploadStatus[document_.id].filename)}
                        >
                          Download
                        </button>
                      </>
                    )}
                    <button
                      className="replace-btn"
                      onClick={() => fileInputRefs.current[document_.id]?.click()}
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <button
                    className="upload-btn-inline"
                    onClick={() => fileInputRefs.current[document_.id]?.click()}
                    disabled={uploadStatus[document_.id]?.uploading}
                    style={{
                      height: '28px',
                      padding: '0 12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploadStatus[document_.id]?.uploading ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {uploadStatus[document_.id]?.uploading ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
            </div>

            <input
              type="file"
              ref={el => { fileInputRefs.current[document_.id] = el; }}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(document_.id, file);
                e.target.value = '';
              }}
            />

            {uploadStatus[document_.id]?.error && (
              <div className="upload-error">{uploadStatus[document_.id].error}</div>
            )}
          </div>
        ))}

        {/* Additional files — same pattern as the Upload Documents page */}
        <div className="document-card">
          <div className="document-header">
            <div className="document-title-block">
              <h4>Additional Documents</h4>
            </div>
            <div className="document-header-actions">
              <span className="status-indicator optional">Optional</span>
            </div>
          </div>

          <div className="biosketches-section">
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
              Anything else the agency asked for. Add a note describing each file so we know what it is.
            </p>

            {additionalDocuments.length > 0 && (
              <div className="biosketches-list">
                <h5>Uploaded Documents</h5>
                {additionalDocuments.map(doc => (
                  <div key={doc.id} className="biosketch-item">
                    <div className="biosketch-info">
                      {editingAdditionalDocId === doc.id ? (
                        <textarea
                          className="note-editor"
                          value={editingAdditionalDocNotes}
                          onChange={(e) => setEditingAdditionalDocNotes(e.target.value)}
                          placeholder="Tell us what this document is"
                          rows={2}
                        />
                      ) : (
                        <span className="personnel-name">{doc.notes || 'No note provided'}</span>
                      )}
                      <span className="biosketch-meta">
                        {[
                          doc.filename.replace(/^JIT_Additional_\d+_/, ''),
                          doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : null
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div className="biosketch-actions">
                      {editingAdditionalDocId === doc.id ? (
                        <>
                          <button
                            className="view-btn small"
                            disabled={savingAdditionalDocNotes}
                            onClick={() => handleSaveAdditionalDocNotes(doc.id)}
                          >
                            {savingAdditionalDocNotes ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="replace-btn small"
                            disabled={savingAdditionalDocNotes}
                            onClick={() => {
                              setEditingAdditionalDocId(null);
                              setEditingAdditionalDocNotes('');
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="edit-btn small"
                            onClick={() => {
                              setEditingAdditionalDocId(doc.id);
                              setEditingAdditionalDocNotes(doc.notes || '');
                            }}
                          >
                            Edit Note
                          </button>
                          {doc.fileUrl && (
                            <>
                              {!isXlsxFile(doc.filename) && (
                                <button
                                  className="view-btn small"
                                  onClick={() => window.open(doc.fileUrl, '_blank')}
                                >
                                  View
                                </button>
                              )}
                              <button
                                className="download-btn small"
                                onClick={() => handleFileDownload(doc.fileUrl, doc.filename)}
                              >
                                Download
                              </button>
                            </>
                          )}
                          <button
                            className="remove-btn small"
                            onClick={() => handleRemoveAdditionalDocument(doc.id)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="additional-doc-upload-box">
              <div className="additional-doc-upload-row">
                <span className="additional-doc-upload-label">Add a document</span>
                <input
                  type="file"
                  ref={additionalDocFileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAdditionalDocumentUpload(file, additionalDocNotes);
                  }}
                />
                <button
                  className="add-biosketch-btn"
                  disabled={additionalDocUploading}
                  onClick={() => additionalDocFileInputRef.current?.click()}
                >
                  {additionalDocUploading ? 'Uploading...' : '+ Upload (Optional)'}
                </button>
              </div>
              <div style={{ marginTop: '10px' }}>
                <label
                  htmlFor="jit-additional-doc-notes"
                  style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}
                >
                  Notes:
                </label>
                <textarea
                  id="jit-additional-doc-notes"
                  value={additionalDocNotes}
                  onChange={(e) => setAdditionalDocNotes(e.target.value)}
                  placeholder="Describe the document before adding the file"
                  rows={2}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrantJITDocuments;
